import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { FaChevronDown } from "react-icons/fa";
import { FilterCreditVouchers } from "../types";
import { useGetAllOrganizationsQuery } from "src/features/organizations/organization.service";
import { CURRENCY_CODES } from "src/utils/constants.util";

type CoachRecordingFilterProps = {
  updateFilterQuery: (where: FilterCreditVouchers) => void;
};

type SelectOption = { value: string; text: string };
type SearchItemConfig = { type: "input" | "select"; placeholder: string; options?: SelectOption[] };

const formatLabel = (key: string) => {
  switch (key) {
    case "voucherId":
      return "Voucher ID";
    case "voucherCode":
      return "Voucher Code";
    case "userId":
      return "User ID";
    default:
      return key.charAt(0).toUpperCase() + key.slice(1);
  }
};

export const CoachRecordingFilter: React.FC<CoachRecordingFilterProps> = ({ updateFilterQuery }) => {
  const { data: organizations } = useGetAllOrganizationsQuery();
  const ref = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPendingSearch = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  const searchItems = useMemo<Record<string, SearchItemConfig>>(
    () => ({
      voucherId: {
        type: "input",
        placeholder: "Enter voucher ID",
      },
      voucherCode: {
        type: "input",
        placeholder: "Enter voucher code",
      },
      userId: {
        type: "input",
        placeholder: "Enter redeemed user ID",
      },
      status: {
        type: "select",
        placeholder: "Choose status",
        options: [
          { value: "AVAILABLE", text: "Available" },
          { value: "REDEEMED", text: "Redeemed" },
        ],
      },
      currency: {
        type: "select",
        placeholder: "Choose currency",
        options: CURRENCY_CODES.map((currency) => ({
          value: currency,
          text: currency,
        })),
      },
      type: {
        type: "select",
        placeholder: "Choose voucher type",
        options: [
          { value: "TOP_UP", text: "Top up" },
          { value: "PROMO", text: "Promo" },
          { value: "PRIVATE_MODE", text: "Private mode" },
          { value: "PREMIUM", text: "Premium" },
          { value: "MONTHLY_PREMIUM", text: "Monthly premium" },
          { value: "YEARLY_PREMIUM", text: "Yearly premium" },
        ],
      },
      organization: {
        type: "select",
        placeholder: "Choose organization",
        options: (organizations ?? []).map((organization) => ({
          value: organization.id,
          text: organization.name,
        })),
      },
    }),
    [organizations]
  );

  const items = Object.keys(searchItems) as (keyof typeof searchItems)[];
  const [selected, setSelected] = useState<keyof typeof searchItems>(items[0]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [activeBadges, setActiveBadges] = useState<{ key: keyof typeof searchItems; label: string; value: string }[]>([]);

  const currentConfig = searchItems[selected];

  const buildFilter = useCallback((key: keyof typeof searchItems, value: string): FilterCreditVouchers => {
    const trimmedValue = value.trim();

    return {
      id: key === "voucherId" ? trimmedValue || undefined : undefined,
      code: key === "voucherCode" ? trimmedValue || undefined : undefined,
      redeemedBy: key === "userId" ? trimmedValue || undefined : undefined,
      organizationId: key === "organization" ? trimmedValue || undefined : undefined,
      type: key === "type" ? ((trimmedValue || undefined) as FilterCreditVouchers["type"]) : undefined,
      currency: key === "currency" ? ((trimmedValue || undefined) as FilterCreditVouchers["currency"]) : undefined,
      status: key === "status" ? ((trimmedValue || undefined) as FilterCreditVouchers["status"]) : undefined,
    };
  }, []);

  const handleSearch = useCallback(
    (key: keyof typeof searchItems, value: string) => {
      const nextFilters = buildFilter(key, value);
      updateFilterQuery(nextFilters);

      if (!value.trim()) {
        setActiveBadges([]);
        return;
      }

      const config = searchItems[key];
      const prettyValue =
        config.type === "select" ? config.options?.find((option) => option.value === value)?.text || value : value;

      setActiveBadges([{ key, label: formatLabel(String(key)), value: prettyValue }]);
    },
    [buildFilter, searchItems, updateFilterQuery]
  );

  const debounceSearch = useCallback(
    (key: keyof typeof searchItems, value: string) => {
      clearPendingSearch();
      debounceRef.current = setTimeout(() => handleSearch(key, value), 300);
    },
    [clearPendingSearch, handleSearch]
  );

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    setQuery("");
    clearPendingSearch();
  }, [clearPendingSearch, selected]);

  useEffect(() => {
    return clearPendingSearch;
  }, [clearPendingSearch]);

  const handleInputChange = (value: string) => {
    setQuery(value);
    if (currentConfig.type === "input") debounceSearch(selected, value);
    else {
      clearPendingSearch();
      handleSearch(selected, value);
    }
  };

  const clearFilters = () => {
    clearPendingSearch();
    setQuery("");
    setActiveBadges([]);
    updateFilterQuery({});
  };

  const renderInputField = () => {
    if (currentConfig.type === "select" && currentConfig.options) {
      return (
        <select
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className="w-full px-6 py-4 text-slate-800 bg-transparent focus:outline-none text-lg border-none focus:text-emerald-700 transition-colors duration-200"
        >
          <option value="">{currentConfig.placeholder}</option>
          {currentConfig.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.text}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        id="credit-voucher-search-dropdown"
        type="search"
        value={query}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={currentConfig.placeholder}
        className="w-full px-6 py-4 text-slate-800 placeholder-slate-500 bg-transparent focus:border-none focus:outline-none focus:ring-0 text-lg flex-grow border-none focus:text-emerald-700 transition-colors duration-200"
      />
    );
  };

  return (
    <div className="flex items-center justify-center pb-7 pt-5">
      <div className="w-full max-w-4xl mx-auto">
        <div ref={ref} className="relative group">
          <label htmlFor="credit-voucher-search-dropdown" className="sr-only">
            Search category
          </label>

          <div
            className={`
              flex rounded-2xl overflow-hidden transition-all duration-300 ease-out
              bg-white/95 backdrop-blur-sm border-2 shadow-xl
              ${
                focused || open
                  ? "border-emerald-400 shadow-2xl shadow-emerald-200/60 scale-[1.02] ring-4 ring-emerald-100/50"
                  : "border-slate-200 hover:border-emerald-300 hover:shadow-2xl hover:scale-[1.01]"
              }
            `}
          >
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className={`
                flex items-center px-6 py-4 bg-gradient-to-r transition-all duration-200
                ${
                  open
                    ? "from-emerald-500 via-teal-500 to-cyan-500 text-white shadow-lg"
                    : "from-slate-100 via-slate-50 to-emerald-50 text-slate-700 hover:from-emerald-100 hover:via-teal-100 hover:to-cyan-100 hover:text-emerald-800"
                }
                border-r border-slate-200 font-medium
              `}
            >
              <span className="truncate">{formatLabel(String(selected))}</span>
              <FaChevronDown className={`ml-3 h-3 w-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
            </button>

            <div className="relative flex-1">
              {renderInputField()}
              {focused && (
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-50/40 via-teal-50/40 to-cyan-50/40 pointer-events-none border-none" />
              )}
            </div>
          </div>

          <div
            className={`
              absolute top-full left-0 mt-2 z-50 transition-all duration-300 ease-out origin-top w-auto
              bg-white/98 backdrop-blur-sm border border-emerald-200 rounded-2xl shadow-2xl overflow-hidden
              ${open ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 -translate-y-2 pointer-events-none"}
            `}
          >
            <ul className="pl-0 list-none m-0 text-sm">
              {items.map((item, index) => (
                <li key={item}>
                  <button
                    type="button"
                    onClick={() => {
                      clearPendingSearch();
                      setSelected(item);
                      setOpen(false);
                    }}
                    className={`
                      w-full px-20 py-3 text-left transition-all duration-150 flex items-center block
                      ${
                        selected === item
                          ? "bg-gradient-to-r from-emerald-100 via-teal-100 to-cyan-100 text-emerald-800 font-medium border-r-4 border-emerald-400 shadow-sm"
                          : "text-slate-700 hover:bg-gradient-to-r hover:from-emerald-50 hover:via-teal-50 hover:to-cyan-50 hover:text-emerald-700"
                      }
                    `}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <span className="flex-1">{formatLabel(String(item))}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {activeBadges.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {activeBadges.map((badge) => (
              <button
                key={`${badge.key}-${badge.value}`}
                onClick={clearFilters}
                className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs text-cyan-700"
                title={`Remove ${badge.label}`}
              >
                <span className="font-medium">{badge.label}:</span>
                <span>{badge.value}</span>
                <span className="ml-1">x</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
